import React, { useState, useEffect } from 'react';
import { vmService, VMStatus } from '../../services/vmService';
import { PlayArrow, Stop, Refresh, Computer } from '@mui/icons-material';
import { CircularProgress, Alert } from '@mui/material';

export const VMManagement: React.FC = () => {
  const [vmStatus, setVmStatus] = useState<VMStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [operating, setOperating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const status = await vmService.getStatus();
      setVmStatus(status);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch VM status');
      console.error('Error fetching VM status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleStart = async () => {
    try {
      setOperating(true);
      setError(null);
      setSuccessMessage(null);
      const result = await vmService.start();
      setSuccessMessage(result.message);
      // Refresh status after a short delay
      setTimeout(() => fetchStatus(), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start VM');
      console.error('Error starting VM:', err);
    } finally {
      setOperating(false);
    }
  };

  const handleStop = async () => {
    if (!window.confirm('Are you sure you want to stop the Refinitiv fetcher VM? This will pause data collection.')) {
      return;
    }

    try {
      setOperating(true);
      setError(null);
      setSuccessMessage(null);
      const result = await vmService.stop();
      setSuccessMessage(result.message);
      // Refresh status after a short delay
      setTimeout(() => fetchStatus(), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to stop VM');
      console.error('Error stopping VM:', err);
    } finally {
      setOperating(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RUNNING':
        return 'text-green-600 bg-green-50';
      case 'TERMINATED':
        return 'text-gray-600 bg-gray-50';
      case 'STOPPING':
      case 'PROVISIONING':
      case 'STAGING':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'RUNNING':
        return '🟢';
      case 'TERMINATED':
        return '🔴';
      case 'STOPPING':
      case 'PROVISIONING':
      case 'STAGING':
        return '🟡';
      default:
        return '⚪';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Computer className="text-blue-600" fontSize="large" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Refinitiv Fetcher VM</h2>
            <p className="text-sm text-gray-500">Manage the data collection virtual machine</p>
          </div>
        </div>
        <button
          onClick={fetchStatus}
          disabled={loading || operating}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
        >
          <Refresh fontSize="small" />
          Refresh
        </button>
      </div>

      {error && (
        <Alert severity="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" className="mb-4" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {vmStatus && (
        <div className="space-y-6">
          {/* Status Overview */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Status</div>
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full font-semibold ${getStatusColor(vmStatus.status)}`}>
                <span>{getStatusIcon(vmStatus.status)}</span>
                {vmStatus.status}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">VM Name</div>
              <div className="font-semibold text-gray-900">{vmStatus.name}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Zone</div>
              <div className="font-semibold text-gray-900">{vmStatus.zone}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Machine Type</div>
              <div className="font-semibold text-gray-900">{vmStatus.machineType || 'N/A'}</div>
            </div>
          </div>

          {/* Network Info */}
          {vmStatus.networkInterfaces && vmStatus.networkInterfaces.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-2">Network</div>
              <div className="space-y-1">
                <div className="text-sm">
                  <span className="text-gray-600">Internal IP:</span>{' '}
                  <span className="font-mono font-semibold">{vmStatus.networkInterfaces[0].internalIP || 'N/A'}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">External IP:</span>{' '}
                  <span className="font-mono font-semibold">{vmStatus.networkInterfaces[0].externalIP || 'N/A'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-2">Timestamps</div>
            <div className="space-y-1 text-sm">
              {vmStatus.lastStartTimestamp && (
                <div>
                  <span className="text-gray-600">Last Started:</span>{' '}
                  <span className="font-semibold">{new Date(vmStatus.lastStartTimestamp).toLocaleString()}</span>
                </div>
              )}
              {vmStatus.lastStopTimestamp && (
                <div>
                  <span className="text-gray-600">Last Stopped:</span>{' '}
                  <span className="font-semibold">{new Date(vmStatus.lastStopTimestamp).toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Control Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleStart}
              disabled={operating || vmStatus.status === 'RUNNING'}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              style={{ 
                backgroundColor: vmStatus.status === 'RUNNING' ? '#9CA3AF' : '#16A34A',
                color: 'white',
                border: 'none'
              }}
            >
              {operating ? <CircularProgress size={20} style={{ color: 'white' }} /> : <PlayArrow style={{ color: 'white' }} />}
              Start VM
            </button>
            <button
              onClick={handleStop}
              disabled={operating || vmStatus.status === 'TERMINATED'}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              style={{ 
                backgroundColor: vmStatus.status === 'TERMINATED' ? '#9CA3AF' : '#DC2626',
                color: 'white',
                border: 'none'
              }}
            >
              {operating ? <CircularProgress size={20} style={{ color: 'white' }} /> : <Stop style={{ color: 'white' }} />}
              Stop VM
            </button>
          </div>

          <div className="text-xs text-gray-500 italic">
            ⚠️ Stopping the VM will pause Refinitiv data collection. Start it again to resume data fetching.
          </div>
        </div>
      )}
    </div>
  );
};
